import * as d3 from 'd3';
import { computeLayout, type Layout } from './layout';
import type { PiprBundle, PiprForward } from './types';

export type ModeKey = 'rate' | 'delta';

export type Hover =
    | { kind: 'hist'; i: number; j: number | null }
    | { kind: 'fwd'; i: number; j: number | null }
    | null;

export type Svg = d3.Selection<SVGSVGElement, unknown, null, undefined>;
export type G = d3.Selection<SVGGElement, unknown, null, undefined>;

export interface Point {
    i: number;
    date: Date;
    meetingIdx: number;
    anchor: number;
    pending: number;
    implied: number;
    p: number[];
    modal: number;
    flagged: boolean;
}

export interface MeetingMark {
    date: Date;
    idx: number;
}

export interface Model {
    layout: Layout;
    isRate: boolean;
    isLive: boolean;
    asof: Date;
    bucketMs: number;

    deltas: number[];
    step: number;

    domain: [Date, Date];
    vis: Point[];
    x: d3.ScaleTime<number, number>;
    colW: number;
    spanMs: number;
    visMeetings: MeetingMark[];
    hasPending: boolean;

    rowH: number;
    cellH: number;
    yRate: d3.ScaleLinear<number, number>;
    yTicks: number[];
    yDelta: (bp: number) => number;

    yCellHist: (d: Point, j: number) => number;
    yAnchor: (d: Point) => number;
    yDecided: (d: Point) => number;
    yPipr: (d: Point) => number;

    fwd: PiprForward;
    fwdModal: number[];
    nF: number;
    slot: number;
    fwdColW: number;
    xF: (i: number) => number;
    yFwdCell: (i: number, j: number) => number;
    yFwdPoint: (i: number) => number;
    yFwdStart: number;
}

export const MIN_CELL_PERMILLE = 5;

function modalOf(p: readonly number[]): number {
    let k = 0;
    for (let j = 1; j < p.length; j++) if (p[j] > p[k]) k = j;
    return k;
}

export function buildPoints(data: PiprBundle): Point[] {
    const h = data.history;
    return d3.range(h.n).map(i => ({
        i,
        date: new Date(h.ts[i] * 1000),
        meetingIdx: h.meetingIdx[i],
        anchor: h.anchorBp[i],
        pending: h.pendingBp[i] ?? 0,
        implied: h.impliedBp[i],
        p: h.p[i],
        modal: modalOf(h.p[i]),
        flagged: h.flags[i] !== 0,
    }));
}

export function buildMeetingDates(data: PiprBundle): Date[] {
    return data.meetings.map(m => new Date(m.decisionTs * 1000));
}

export function buildFwdModal(data: PiprBundle): number[] {
    return data.forward.meetings.map(m => modalOf(m.p));
}

export interface ModelInput {
    data: PiprBundle;
    points: Point[];
    meetingDates: Date[];
    fwdModal: number[];
    width: number;
    height: number;
    domain: [Date, Date];
    mode: ModeKey;
    isLive: boolean;
}

export function buildModel(input: ModelInput): Model | null {
    const {
        data,
        points,
        meetingDates,
        fwdModal,
        width,
        height,
        domain,
        mode,
    } = input;
    if (width === 0) return null;

    const layout = computeLayout(width, height);
    const { plotTop, plotBottom, marginLeft, split, fwdW } = layout;

    const deltas = data.history.deltas;
    if (deltas.length === 0) return null;
    const step = deltas.length > 1 ? deltas[1] - deltas[0] : 25;
    const fwd = data.forward;
    const bucketMs = data.bucketSec * 1000;

    const [start, end] = domain;
    const x = d3.scaleTime().domain([start, end]).range([marginLeft, split]);
    const t0 = start.getTime() - bucketMs;
    const t1 = end.getTime() + bucketMs;
    const vis = points.filter(d => {
        const t = d.date.getTime();
        return t >= t0 && t <= t1;
    });
    if (vis.length === 0 && fwd.meetings.length === 0) return null;

    const colW = Math.max(
        1,
        x(new Date(start.getTime() + bucketMs)) - x(start) - 0.4
    );

    const lows: number[] = [];
    const highs: number[] = [];
    vis.forEach(d => {
        const decided = d.anchor + d.pending;
        lows.push(Math.min(d.anchor, decided) + deltas[0]);
        highs.push(Math.max(d.anchor, decided) + deltas[deltas.length - 1]);
    });
    fwd.meetings.forEach(m => {
        lows.push(m.baseBp + deltas[0]);
        highs.push(m.baseBp + deltas[deltas.length - 1]);
    });

    const yRate = d3
        .scaleLinear()
        .domain([(d3.min(lows) as number) - 18, (d3.max(highs) as number) + 18])
        .range([plotBottom, plotTop]);

    const rowH = (plotBottom - plotTop) / deltas.length;
    const yRow = d3
        .scaleLinear()
        .domain([0, deltas.length - 1])
        .range([plotBottom - rowH / 2, plotTop + rowH / 2]);
    const yDelta = (bp: number) => yRow((bp - deltas[0]) / step);

    const isRate = mode === 'rate';
    const cellH = isRate ? Math.abs(yRate(0) - yRate(step)) - 2 : rowH - 5;

    const nF = fwd.meetings.length;
    const slot = nF > 0 ? fwdW / nF : fwdW;

    return {
        layout,
        isRate,
        isLive: input.isLive,
        asof: new Date(fwd.asofTs * 1000),
        bucketMs,
        deltas,
        step,
        domain,
        vis,
        x,
        colW,
        spanMs: end.getTime() - start.getTime(),
        visMeetings: meetingDates
            .map((date, idx) => ({ date, idx }))
            .filter(m => m.date >= start && m.date <= end),
        hasPending: vis.some(d => d.pending !== 0),

        rowH,
        cellH,
        yRate,
        yTicks: yRate.ticks(layout.isNarrow ? 4 : 6),
        yDelta,

        yCellHist: (d, j) =>
            isRate
                ? yRate(d.anchor + d.pending + deltas[j])
                : yDelta(deltas[j]),
        yAnchor: d => (isRate ? yRate(d.anchor) : yDelta(-d.pending)),
        yDecided: d => (isRate ? yRate(d.anchor + d.pending) : yDelta(0)),
        yPipr: d =>
            isRate
                ? yRate(d.implied)
                : yDelta(d.implied - d.anchor - d.pending),

        fwd,
        fwdModal,
        nF,
        slot,
        fwdColW: Math.max(10, Math.min(36, slot - 10)),
        xF: i => split + slot * (i + 0.5),
        yFwdCell: (i, j) =>
            isRate
                ? yRate(fwd.meetings[i].baseBp + deltas[j])
                : yDelta(deltas[j]),
        yFwdPoint: i =>
            isRate
                ? yRate(fwd.meetings[i].rateBp)
                : yDelta(fwd.meetings[i].rateBp - fwd.meetings[i].baseBp),
        yFwdStart: isRate ? yRate(fwd.anchorBp + fwd.pendingBp) : yDelta(0),
    };
}

export function nearestPoint(model: Model, px: number): Point | null {
    if (model.vis.length === 0) return null;
    const t = model.x.invert(px);
    const bisect = d3.bisector<Point, Date>(d => d.date).center;
    return model.vis[bisect(model.vis, t)] ?? null;
}

export function forwardIndexAt(model: Model, px: number): number | null {
    if (model.nF === 0) return null;
    const i = Math.floor((px - model.layout.split) / model.slot);
    return Math.max(0, Math.min(model.nF - 1, i));
}

function cellIndex(
    p: readonly number[],
    yOf: (j: number) => number,
    cellH: number,
    py: number
): number | null {
    let best: number | null = null;
    let dist = Infinity;
    for (let j = 0; j < p.length; j++) {
        if (p[j] < MIN_CELL_PERMILLE) continue;
        const d = Math.abs(yOf(j) - py);
        if (d <= Math.max(2, cellH) / 2 + 1 && d < dist) {
            best = j;
            dist = d;
        }
    }
    return best;
}

export function histCellAt(model: Model, d: Point, py: number): number | null {
    return cellIndex(d.p, j => model.yCellHist(d, j), model.cellH, py);
}

export function fwdCellAt(model: Model, i: number, py: number): number | null {
    return cellIndex(
        model.fwd.meetings[i].p,
        j => model.yFwdCell(i, j),
        model.cellH,
        py
    );
}
